<script setup>
/* Creazione e modifica di un pin.
 *
 * Corregge tre difetti dell'originale:
 * - `createPin()` usciva con un `return` silenzioso se non c'erano né URL né
 *   immagine, *dopo* aver aperto lo spinner e senza chiuderlo: il click su
 *   "Create Pin" con form vuoto bloccava l'interfaccia;
 * - `savePin()` non aveva alcun gestore d'errore, quindi un PATCH fallito
 *   lasciava la modale aperta e muta;
 * - creando una board al volo dal selettore, veniva sempre creata pubblica,
 *   ignorando la spunta "privata".
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Board, Pin, Tag } from '@/api';
import { errorMessage, fieldErrors } from '@/api/client';
import BaseModal from '@/components/BaseModal.vue';
import TagInput from '@/components/TagInput.vue';
import { linkify } from '@/utils/linkify';

const props = defineProps({
  pin: { type: Object, default: null },
  /** Precompila i campi (usato dal bookmarklet e dallo userscript). */
  initial: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const isEdit = computed(() => Boolean(props.pin));

const form = ref({
  url: props.pin?.url ?? props.initial.url ?? '',
  referer: props.pin?.referer ?? props.initial.referer ?? '',
  description: props.pin?.description ?? props.initial.description ?? '',
  tags: [...(props.pin?.tags ?? [])],
  private: props.pin?.private ?? false,
});

const uploaded = ref(null); // {id, image, ...} dopo l'upload
const uploadPreview = ref(null); // object URL locale, mostrato subito
const dragging = ref(false);
const busy = ref(false);
const errors = ref({});
const generalError = ref('');

const suggestions = ref([]);
const boards = ref([]);
const selectedBoards = ref([]);
const newBoardName = ref('');

const previewSegments = computed(() => linkify(form.value.description));

/** L'immagine mostrata in anteprima: quella caricata, o l'URL digitato. */
const previewSrc = computed(() => {
  if (uploadPreview.value) return uploadPreview.value;
  if (isEdit.value) return props.pin.image?.standard?.image ?? props.pin.image?.image;
  return null;
});

onMounted(async () => {
  Tag.list()
    .then((list) => {
      suggestions.value = list.map((item) => item.name);
    })
    .catch(() => {});
  if (!isEdit.value) {
    Board.autocomplete()
      .then((list) => {
        boards.value = list;
      })
      .catch(() => {});
  }
});

async function onFile(file) {
  if (!file) return;
  busy.value = true;
  generalError.value = '';
  try {
    uploadPreview.value = URL.createObjectURL(file);
    uploaded.value = await Pin.uploadImage(file);
    // URL e upload sono alternativi: appena c'è un file l'URL non serve più.
    form.value.url = '';
  } catch (error) {
    generalError.value = errorMessage(error, t('pinSaveFailed'));
    uploadPreview.value = null;
  } finally {
    busy.value = false;
  }
}

function onDrop(event) {
  dragging.value = false;
  onFile(event.dataTransfer?.files?.[0]);
}

async function createBoardInline() {
  const name = newBoardName.value.trim();
  if (!name) return;
  try {
    // La board eredita la privacy del pin: creare una board pubblica per un
    // pin privato è quasi sempre un errore.
    const board = await Board.create(name, form.value.private);
    // In coda finirebbe fuori posto: l'elenco arriva alfabetico dall'API e una
    // board appena creata deve sedersi al suo posto, non in fondo. Stessa
    // comparazione del server (maiuscole ignorate).
    boards.value = [...boards.value, board].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
    selectedBoards.value = [...selectedBoards.value, board.id];
    newBoardName.value = '';
  } catch (error) {
    generalError.value = errorMessage(error, t('boardSaveFailed'));
  }
}

async function submit() {
  if (busy.value) return;
  errors.value = {};
  generalError.value = '';

  if (!isEdit.value && !form.value.url.trim() && !uploaded.value) {
    generalError.value = t('pinCreateModalImageURLPlaceholder');
    return; // niente spinner aperto e mai chiuso
  }

  busy.value = true;
  try {
    let saved;
    if (isEdit.value) {
      saved = await Pin.update(props.pin.id, {
        referer: form.value.referer,
        description: form.value.description,
        tags: form.value.tags,
        private: form.value.private,
      });
    } else {
      const payload = {
        referer: form.value.referer,
        description: form.value.description,
        tags: form.value.tags,
        private: form.value.private,
      };
      if (uploaded.value) payload.image_by_id = uploaded.value.id;
      else payload.url = form.value.url.trim();
      saved = await Pin.create(payload);

      if (selectedBoards.value.length) {
        await Promise.all(selectedBoards.value.map((id) => Board.addPins(id, [saved.id])));
      }
    }
    emit('close', saved);
  } catch (error) {
    errors.value = fieldErrors(error);
    generalError.value = errorMessage(error, t('pinSaveFailed'));
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <BaseModal
    size="wide"
    :title="isEdit ? $t('EditPinTitle') : $t('NewPinTitle')"
    @close="emit('close')"
  >
    <form id="pin-form" class="editor" @submit.prevent="submit">
      <div class="editor__col">
        <template v-if="!isEdit">
          <label v-if="!uploaded" class="field">
            <span class="field__label">{{ $t('imageUrlLabel') }}</span>
            <input
              v-model="form.url"
              class="input"
              :class="{ 'input--invalid': errors.url }"
              type="url"
              autofocus
              maxlength="2048"
              :placeholder="$t('pinCreateModalImageURLPlaceholder')"
            />
            <span v-if="errors.url" class="field__error">{{ errors.url }}</span>
          </label>

          <div
            v-if="!form.url"
            class="drop"
            :class="{ 'is-dragging': dragging }"
            @dragover.prevent="dragging = true"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
          >
            <input
              id="pin-file"
              class="visually-hidden"
              type="file"
              accept="image/*"
              @change="onFile($event.target.files[0])"
            />
            <label class="drop__label" for="pin-file">{{ $t('FileUploadDescription') }}</label>
          </div>
        </template>

        <figure v-if="previewSrc" class="preview">
          <img :src="previewSrc" alt="" />
          <figcaption v-if="previewSegments.length" class="preview__caption">
            <template v-for="(segment, index) in previewSegments" :key="index">
              <a v-if="segment.type === 'link'" :href="segment.href" target="_blank" rel="noopener noreferrer">{{
                segment.label
              }}</a
              ><template v-else>{{ segment.value }}</template>
            </template>
          </figcaption>
        </figure>
      </div>

      <div class="editor__col">
        <label class="field">
          <span class="field__label">{{ $t('imageSourceLabel') }}</span>
          <input
            v-model="form.referer"
            class="input"
            type="url"
            maxlength="2048"
            :placeholder="$t('pinCreateModalImageSourcePlaceholder')"
          />
        </label>

        <label class="field">
          <span class="field__label">{{ $t('descriptionLabel') }}</span>
          <textarea
            v-model="form.description"
            class="textarea"
            maxlength="1024"
            :placeholder="$t('pinCreateModalImageDescriptionPlaceholder')"
          />
        </label>

        <div class="field">
          <span class="field__label">{{ $t('tagsLabel') }}</span>
          <TagInput
            v-model="form.tags"
            :suggestions="suggestions"
            :placeholder="$t('pinCreateModalImageTagsPlaceholder')"
          />
        </div>

        <label class="check">
          <input v-model="form.private" type="checkbox" />
          <span>{{ $t('isPrivateCheckbox') }}</span>
        </label>

        <div v-if="!isEdit" class="field">
          <span class="field__label">{{ $t('selectBoardLabel') }}</span>
          <ul v-if="boards.length" class="boards">
            <li v-for="board in boards" :key="board.id">
              <label class="check">
                <input v-model="selectedBoards" type="checkbox" :value="board.id" />
                <span>{{ board.name }}</span>
              </label>
            </li>
          </ul>
          <p v-else class="hint label">{{ $t('pinCreateModalEmptySlot') }}</p>

          <div class="newboard">
            <input
              v-model="newBoardName"
              class="input"
              type="text"
              maxlength="128"
              :placeholder="$t('filterSelectSelectBoardPlaceholder')"
              @keydown.enter.prevent="createBoardInline"
            />
            <button class="btn" type="button" :disabled="!newBoardName.trim()" @click="createBoardInline">
              {{ $t('filterSelectCreateNewBoardButton') }}
            </button>
          </div>
        </div>

        <p v-if="generalError" class="field__error" role="alert">{{ generalError }}</p>
      </div>
    </form>

    <template #footer>
      <button class="btn" type="button" @click="emit('close')">{{ $t('cancelButton') }}</button>
      <button class="btn btn--primary" type="submit" form="pin-form" :disabled="busy">
        {{ isEdit ? $t('pinCreateModalSaveChangesButton') : $t('pinCreateModalCreatePinButton') }}
      </button>
    </template>
  </BaseModal>
</template>

<style scoped>
.editor {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--s-5);
}

@media (max-width: 720px) {
  .editor {
    grid-template-columns: minmax(0, 1fr);
  }
}

.drop {
  display: grid;
  place-items: center;
  padding: var(--s-6) var(--s-4);
  border: 1px dashed var(--rule-strong);
  border-radius: var(--radius);
  text-align: center;
}

.drop.is-dragging {
  border-color: var(--accent);
  background: var(--accent-wash);
}

.drop__label {
  color: var(--ink-muted);
  font-size: var(--t-sm);
  cursor: pointer;
}

.preview {
  margin: var(--s-4) 0 0;
}

.preview img {
  width: 100%;
  border: 1px solid var(--rule);
  border-radius: var(--radius);
}

.preview__caption {
  margin-top: var(--s-2);
  color: var(--ink-muted);
  font-size: var(--t-sm);
  overflow-wrap: anywhere;
}

.preview__caption a {
  color: var(--accent);
  text-decoration: underline;
}

.check {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  margin-bottom: var(--s-3);
  font-size: var(--t-sm);
  cursor: pointer;
}

.boards {
  max-height: 9rem;
  overflow-y: auto;
  padding: var(--s-2);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
}

.boards .check {
  margin-bottom: var(--s-1);
}

.newboard {
  display: flex;
  gap: var(--s-2);
  margin-top: var(--s-2);
}

.hint {
  padding: var(--s-2) 0;
}
</style>
